import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Archive } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "@/lib/toast";

const INP = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm";

function Blockers({ b }) {
  const rows = [
    ["open_orders", "open order(s) still in progress"],
    ["accepted_offers", "accepted offer(s) awaiting checkout"],
    ["open_disputes", "open dispute(s)"],
  ].filter(([k]) => b?.[k]);
  if (!rows.length) return null;
  return (
    <div className="border border-amber-400/40 bg-amber-400/5 p-3 text-xs font-mono text-amber-300 space-y-1" data-testid="close-account-blockers">
      <div className="uppercase text-[10px] tracking-wide">Finish these first</div>
      {rows.map(([k, label]) => <div key={k} data-testid={`close-account-blocker-${k}`}>• {b[k]} {label}</div>)}
    </div>
  );
}

// Member self-archive ("close my account"). Terminal for the member — only an admin can reverse it.
export function CloseAccountSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/account/archive/eligibility").then(({ data }) => setElig(data)).catch(() => {}); }, []);
  if (!elig || elig.is_staff) return null;
  const ready = elig.eligible && password && confirm === "ARCHIVE" && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError("");
    try {
      await api.post("/account/archive", { password, confirm, reason: reason.trim() || undefined });
      toast.success("Your account has been closed. A confirmation email is on its way.");
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border border-blaze/40 bg-surface p-5" data-testid="close-account-section">
      <div className="flex items-center gap-2">
        <Archive className="w-5 h-5 text-blaze" />
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Close account</h2>
      </div>
      <p className="text-sm text-secondary-foreground font-body mt-1">
        Archives your account: you're signed out everywhere, your listings are hidden, your membership is canceled
        immediately (no refund, no further charges) and saved payment methods are removed. Your data is kept and
        your username stays reserved. Only an OpticSwap admin can reopen it.
      </p>
      <Blockers b={elig.blockers} />
      {!elig.has_password && (
        <p className="mt-3 text-xs font-mono text-amber-300" data-testid="close-account-needs-password">Set a password above first — closing your account requires re-entering it.</p>
      )}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} disabled={!elig.eligible} data-testid="close-account-open-btn"
          className="mt-4 font-mono uppercase text-xs px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Close my account
        </button>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="close-account-form">
          {error && <div data-testid="close-account-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono flex gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{error}</div>}
          <div>
            <label htmlFor="close-pw" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Current password</label>
            <div className="mt-1">
              <PasswordInput id="close-pw" testId="close-account-password" inputTestId="close-account-password" toggleTestId="close-account-password-toggle"
                value={password} onChange={setPassword} autoComplete="current-password" />
            </div>
          </div>
          <div>
            <label htmlFor="close-reason" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Why are you leaving? <span className="normal-case text-muted-foreground">(optional)</span></label>
            <textarea id="close-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2}
              className={`${INP} mt-1 resize-none`} data-testid="close-account-reason" />
          </div>
          <div>
            <label htmlFor="close-confirm" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Type ARCHIVE to confirm</label>
            <input id="close-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off"
              className={`${INP} mt-1`} placeholder="ARCHIVE" data-testid="close-account-confirm" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={!ready} data-testid="close-account-submit"
              className="font-heading font-bold uppercase text-sm px-4 py-2.5 bg-blaze text-white hover:bg-blaze/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {busy ? "Closing…" : "Permanently close my account"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setPassword(""); setConfirm(""); setError(""); }} data-testid="close-account-cancel"
              className="font-mono uppercase text-xs px-3 py-2 text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
